import { ArrowRightIcon, MobileIcon, ReloadIcon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { IHospitalDetail } from "../../../../@types/admin/AdminHospitalDetails";
import { IAdminPatientListItem } from "../../../../@types/admin/AdminPatient";
import { IWard } from "../../../../@types/admin/AdminWard";
import { TableSorted } from "../../../../components/TableSorted";
import {
  HOSPITAL_PATIENTS_QUERY_KEY,
  useHospitalPatients,
} from "../../../../hooks/queries/admin/hospitalDetails";
import { useDebounce } from "../../../../hooks/useDebounce";
import { useModal } from "../../../../hooks/useModal";
import { getLongFormattedDate } from "../../../../utils/dateFormatter";
import { RowActions } from "../../Patients/AdminPatients.styles";
import { passwordSetTone } from "../../Patients/patientListFields";
import {
  EmailLink,
  ErrorMessage,
  FieldHint,
  FilterButton,
  FiltersActions,
  FiltersContainer,
  PatientDevicesPanel,
  RowAction,
  SearchInput,
  SectionCaption,
  SectionCard,
  SectionHeader,
  SectionHeading,
  SectionTitle,
  StatusBadge,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
} from "../../shared";
import { AddPatientModal } from "./AddPatientModal";
import { TabStack } from "./HospitalDetails.styles";
import { RefreshWorkflowConfigModal } from "./RefreshWorkflowConfigModal";

const DEFAULT_PAGE_SIZE = 10;

interface IHospitalPatientsTabProps {
  hospital: IHospitalDetail;
  wards: IWard[];
}

/**
 * Patients tab of the Hospital Details page (EN-5172) — Retool's
 * `#### Patients` container plus its `#### Patient devices` one.
 *
 * Retool's patient list came from `getHospitalPatients.sql`, which read the ward
 * ids **out of the page's own wards table** and passed them as an array. So the
 * list was empty until the wards query had run, and it silently paged nothing if
 * a ward had been added since. The port asks the API for the hospital's patients
 * directly.
 *
 * The devices table is the shared `PatientDevicesPanel` (EN-5166), keyed on the
 * selected patient. Retool had its own `GetPatientDevices.sql` here — the same
 * per-patient query `moduleDevices` already ran, keyed on
 * `tablePatients.selectedRow.id`, so no new endpoint was needed. The ticket
 * describes it as "devices for a hospital's patients"; it is one patient's.
 */
export function HospitalPatientsTab({
  hospital,
  wards,
}: IHospitalPatientsTabProps) {
  const hospitalId = Number(hospital.id);
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedPatient, setSelectedPatient] =
    useState<IAdminPatientListItem | null>(null);

  const debouncedSearch = useDebounce(searchTerm, 400);

  const { patients, totalCount, isFetching, isError } = useHospitalPatients(
    hospitalId,
    page,
    pageSize,
    debouncedSearch
  );

  const columns: ColumnDef<IAdminPatientListItem, string>[] = useMemo(
    () => [
      {
        id: "id",
        header: "ID",
        accessorFn: (patient) => String(patient.id),
        enableSorting: true,
      },
      { accessorKey: "name", header: "Name", enableSorting: true },
      {
        accessorKey: "email",
        header: "Email",
        cell: ({ row }) => (
          <EmailLink
            href={`mailto:${row.original.email}`}
            onClick={(e) => e.stopPropagation()}
          >
            {row.original.email}
          </EmailLink>
        ),
        enableSorting: true,
      },
      { accessorKey: "mrn", header: "MRN", enableSorting: true },
      { accessorKey: "ward", header: "Primary ward", enableSorting: true },
      {
        accessorKey: "created",
        header: "Created",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.created, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "lastUsed",
        header: "Last used",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.lastUsed, "Never")}</span>
        ),
        enableSorting: true,
      },
      {
        id: "passwordSet",
        header: "Password",
        accessorFn: (patient) => patient.passwordSet || "-",
        cell: ({ row }) => (
          <StatusBadge status={passwordSetTone(row.original.passwordSet)}>
            {row.original.passwordSet || "-"}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        id: "archived",
        header: "State",
        accessorFn: (patient) => (patient.archived ? "Archived" : "Active"),
        cell: ({ row }) => (
          <StatusBadge status={row.original.archived ? "inactive" : "active"}>
            {row.original.archived ? "Archived" : "Active"}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions>
            <RowAction
              label="Devices"
              icon={<MobileIcon />}
              onClick={() => {
                setSelectedPatient(row.original);
              }}
            />
            <RowAction
              label="Refresh workflow"
              icon={<ReloadIcon />}
              onClick={() => {
                openModal(
                  <RefreshWorkflowConfigModal
                    patient={row.original}
                    onDone={closeModal}
                  />,
                  { width: "520px", showCloseButton: true }
                );
              }}
            />
            <RowAction
              label="Open"
              icon={<ArrowRightIcon />}
              onClick={() => {
                navigate(`/admin/patients/${row.original.id}`);
              }}
            />
          </RowActions>
        ),
      },
    ],
    []
  );

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <TabStack>
      <SectionCard>
        <SectionHeader>
          <SectionHeading>
            <SectionTitle>Patients</SectionTitle>
            <SectionCaption>
              {totalCount} across every ward of this hospital
            </SectionCaption>
          </SectionHeading>
        </SectionHeader>

        {wards.length === 0 && (
          <FieldHint>
            This hospital has no wards yet, so a patient cannot be added — a
            patient belongs to a ward.
          </FieldHint>
        )}

        <FiltersContainer>
          <SearchInput
            type="text"
            placeholder="Search by name, email, or MRN..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
          />
          <FiltersActions>
            <FilterButton
              variant="primary"
              disabled={wards.length === 0}
              onClick={() =>
                openModal(
                  <AddPatientModal
                    hospital={hospital}
                    wards={wards}
                    onDone={closeModal}
                  />,
                  {
                    width: "min(640px, 94vw)",
                    maxHeight: "90vh",
                    showCloseButton: true,
                  }
                )
              }
            >
              Add patient
            </FilterButton>
          </FiltersActions>
        </FiltersContainer>

        {isError && (
          <ErrorMessage>
            Failed to load this hospital&apos;s patients. Try refreshing the
            page.
          </ErrorMessage>
        )}

        <TableContainer>
          <TableWrapper refreshKeys={[[HOSPITAL_PATIENTS_QUERY_KEY]]}>
            <TableSorted
              columns={columns}
              data={patients}
              emptyStateText={
                isFetching ? <TableLoadingState /> : "No rows found"
              }
              onRowClick={(patient) => setSelectedPatient(patient)}
            />
          </TableWrapper>
          <TablePagination
            pagination={{
              currentPage: page,
              pageSize,
              totalPages,
              totalItems: totalCount,
              hasNextPage: page < totalPages,
              hasPreviousPage: page > 1,
            }}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            exportCsv={{
              filename: `hospital-${hospitalId}-patients`,
              columns,
              // Server-paginated: only the fetched page can be written.
              rows: patients,
              currentPageOnly: true,
            }}
          />
        </TableContainer>
      </SectionCard>

      <SectionCard>
        {selectedPatient ? (
          <PatientDevicesPanel
            patientId={selectedPatient.id}
            title={`Devices — ${
              selectedPatient.name || `patient ${selectedPatient.id}`
            }`}
          />
        ) : (
          <>
            <SectionHeader>
              <SectionHeading>
                <SectionTitle>Patient devices</SectionTitle>
              </SectionHeading>
            </SectionHeader>
            <FieldHint>
              Select a patient above to see and manage their devices. Retool
              showed an empty table until a row was selected; this says why.
            </FieldHint>
          </>
        )}
      </SectionCard>
    </TabStack>
  );
}
